import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Footer from "./home/Footer";
import {
  Layers,
  Cloud,
  Target,
  Rocket,
  Code2,
  Headphones,
  Search,
  Map,
  PenTool,
  Hammer,
  ShieldCheck,
  CloudUpload,
  TrendingUp,
} from "lucide-react";

function useReveal() {
  const ref = useRef();

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("in");
          obs.unobserve(el);
        }
      },
      {
        threshold: 0.06,
      }
    );

    obs.observe(el);

    return () => obs.disconnect();
  }, []);

  return ref;
}

/*
 * CAPABILITIES
 */
const capabilities = [
  "Full ownership: design, frontend, backend & deployment",
  "Direct access to the team building your product — no account managers",
  "Modern, scalable stack from day one (React, Next.js, Spring Boot, AWS)",
];

/*
 * PROCESS STEPS
 */
const processSteps = [
  { num: "01", icon: Search, title: "Discovery" },
  { num: "02", icon: Map, title: "Planning" },
  { num: "03", icon: PenTool, title: "UI/UX Design" },
  { num: "04", icon: Hammer, title: "Development" },
  { num: "05", icon: ShieldCheck, title: "Testing" },
  { num: "06", icon: CloudUpload, title: "Deployment" },
  { num: "07", icon: TrendingUp, title: "Support" },
];

export default function About() {
  const r = useReveal();

  return (
    <div
      id="about"
      className="mt-16 w-full overflow-hidden"
    >
      {/* =====================================================
          HERO SECTION
      ===================================================== */}

      <section
        className="
          relative
          h-[260px]

          sm:h-[300px]

          md:h-[320px]
        "
      >
        <img
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&auto=format&fit=crop&q=80"
          alt="About Us"
          className="h-full w-full object-cover"
        />

        <div
          className="
            absolute
            inset-0
            flex
            items-center
            justify-center
            bg-[#1A1714]/70
            px-5
            text-center

            sm:px-6
          "
        >
          <div className="mx-auto max-w-5xl">
            <div className="mb-4 flex items-center justify-center gap-2">
              <span className="h-px w-5 bg-[#C8762A] sm:w-6" />

              <p
                className="
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.25em]
                  text-[#C8762A]

                  sm:text-[11px]
                  sm:tracking-[0.3em]
                "
              >
                About Us
              </p>

              <span className="h-px w-5 bg-[#C8762A] sm:w-6" />
            </div>

            <h1
              className="
                text-[30px]
                font-bold
                leading-[1.15]
                text-white
                drop-shadow-lg

                sm:text-4xl

                md:text-5xl

                lg:text-6xl
              "
            >
              Design as a{" "}
              <span className="italic text-[#C8762A]">
                Competitive Advantage
              </span>
            </h1>
          </div>
        </div>
      </section>

      {/* =====================================================
          WHO WE ARE
      ===================================================== */}

      <section
        className="
          bg-gradient-to-b
          from-[#FAF7F2]
          to-white
          px-5
          py-12

          sm:px-6
          sm:py-16

          md:px-10

          lg:px-20
          lg:py-24
        "
      >
        <div
          ref={r}
          className="
            reveal
            mx-auto
            grid
            max-w-7xl
            items-center
            gap-10

            md:grid-cols-2
            md:gap-12

            lg:gap-16
          "
        >
          {/* Content */}
          <div>
            <p
              className="
                mb-3
                text-[10px]
                font-bold
                uppercase
                tracking-[0.25em]
                text-[#C8762A]

                sm:mb-4
                sm:text-[11px]
                sm:tracking-[0.3em]
              "
            >
              Who We Are
            </p>

            <h2
              className="
                mb-5
                text-[28px]
                font-bold
                leading-tight
                text-[#1A1714]

                sm:text-3xl

                md:text-4xl
              "
            >
              A Studio Built for Fast-Moving Teams
            </h2>

            <p
              className="
                mb-4
                text-sm
                leading-6
                text-stone-600

                sm:text-base
                sm:leading-relaxed
              "
            >
              We're a full-stack engineering and design studio building
              high-performance web and mobile products for early-stage and
              growing businesses. From architecture to launch, we own the
              entire technical stack — Java Spring Boot backends, React/Next.js
              frontends, and cloud-native infrastructure that scales as you do.
            </p>

            <p
              className="
                text-sm
                leading-6
                text-stone-600

                sm:text-base
                sm:leading-relaxed
              "
            >
              As a newer studio, we bring something larger agencies often lose:
              founder-level attention on every project, no layers of account
              managers, and a stack chosen for where your product is headed —
              not legacy tooling we're stuck maintaining.
            </p>

            {/* Capabilities */}
            <div className="mt-7 space-y-4 sm:mt-10">
              {capabilities.map((capability) => (
                <div
                  key={capability}
                  className="flex items-start gap-3 sm:gap-4"
                >
                  <div
                    className="
                      flex
                      h-8
                      w-8
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      bg-[#FBF0E2]
                      text-[#C8762A]

                      sm:h-9
                      sm:w-9
                    "
                  >
                    <Rocket className="h-4 w-4" />
                  </div>

                  <span
                    className="
                      pt-1
                      text-sm
                      font-medium
                      leading-5
                      text-[#3D3830]

                      sm:text-base
                      sm:leading-6
                    "
                  >
                    {capability}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="relative pb-12 sm:pb-14 md:pb-0">
            <img
              src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&auto=format&fit=crop&q=80"
              alt="Our Team"
              className="
                h-[300px]
                w-full
                rounded-2xl
                object-cover
                shadow-lg
                transition

                sm:h-[400px]
                sm:rounded-3xl

                md:h-[460px]

                lg:h-[520px]
                lg:rounded-[32px]
                lg:hover:shadow-2xl
              "
            />

            {/* Floating Card */}
            <div
              className="
                absolute
                -bottom-1
                left-4
                right-4
                rounded-2xl
                bg-white
                p-4
                shadow-xl

                sm:-bottom-2
                sm:left-auto
                sm:right-4
                sm:w-auto
                sm:p-5

                md:-bottom-6
                md:-right-4

                lg:-bottom-8
                lg:-right-8
                lg:p-7
              "
            >
              <h3
                className="
                  text-lg
                  font-bold
                  text-[#1A1714]

                  sm:text-xl

                  lg:text-2xl
                "
              >
                Early-Stage,
              </h3>

              <p
                className="
                  text-lg
                  font-bold
                  text-[#C8762A]

                  sm:text-xl

                  lg:text-2xl
                "
              >
                Full Commitment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          OUR VALUES
      ===================================================== */}

      <section
        className="
          bg-white
          px-5
          py-12
          text-center

          sm:px-6
          sm:py-16

          lg:py-20
        "
      >
        <p
          className="
            mb-3
            text-[10px]
            font-bold
            uppercase
            tracking-[0.25em]
            text-[#C8762A]

            sm:text-[11px]
            sm:tracking-[0.3em]
          "
        >
          What Drives Us
        </p>

        <h2
          className="
            mb-8
            text-[28px]
            font-bold
            text-[#1A1714]

            sm:text-3xl

            md:mb-12
            md:text-4xl
          "
        >
          Our Core Values
        </h2>

        <div
          className="
            mx-auto
            grid
            max-w-7xl
            grid-cols-1
            gap-4

            sm:grid-cols-2
            sm:gap-6

            md:grid-cols-3

            lg:gap-8
          "
        >
          {/* Engineering Rigor */}
          <div
            className="
              rounded-xl
              bg-[#FAF7F2]
              p-5
              shadow
              transition

              sm:p-6

              lg:p-8
              lg:hover:-translate-y-2
              lg:hover:shadow-xl
            "
          >
            <Layers
              className="
                mx-auto
                mb-3
                h-10
                w-10
                text-[#C8762A]

                sm:h-12
                sm:w-12

                lg:mb-4
                lg:h-14
                lg:w-14
              "
            />

            <h3 className="mb-2 text-lg font-semibold text-[#1A1714] sm:text-xl">
              Engineering Rigor
            </h3>

            <p className="text-sm leading-6 text-stone-600 sm:text-base">
              Clean, scalable architecture and code reviewed at every step.
            </p>
          </div>

          {/* Built to Scale */}
          <div
            className="
              rounded-xl
              bg-[#FAF7F2]
              p-5
              shadow
              transition

              sm:p-6

              lg:p-8
              lg:hover:-translate-y-2
              lg:hover:shadow-xl
            "
          >
            <Cloud
              className="
                mx-auto
                mb-3
                h-10
                w-10
                text-[#C8762A]

                sm:h-12
                sm:w-12

                lg:mb-4
                lg:h-14
                lg:w-14
              "
            />

            <h3 className="mb-2 text-lg font-semibold text-[#1A1714] sm:text-xl">
              Built to Scale
            </h3>

            <p className="text-sm leading-6 text-stone-600 sm:text-base">
              Cloud-native infrastructure that grows with your product.
            </p>
          </div>

          {/* Customer Focus */}
          <div
            className="
              rounded-xl
              bg-[#FAF7F2]
              p-5
              shadow
              transition

              sm:col-span-2
              sm:p-6

              md:col-span-1

              lg:p-8
              lg:hover:-translate-y-2
              lg:hover:shadow-xl
            "
          >
            <Target
              className="
                mx-auto
                mb-3
                h-10
                w-10
                text-[#C8762A]

                sm:h-12
                sm:w-12

                lg:mb-4
                lg:h-14
                lg:w-14
              "
            />

            <h3 className="mb-2 text-lg font-semibold text-[#1A1714] sm:text-xl">
              Customer Focus
            </h3>

            <p className="text-sm leading-6 text-stone-600 sm:text-base">
              Dedicated support tailored to your goals, not just your tickets.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================
          OUR PROCESS
      ===================================================== */}

      <section
        className="
          overflow-hidden
          bg-[#FAF7F2]
          px-4
          py-14

          sm:px-6
          sm:py-20

          lg:px-20
          lg:py-28
        "
      >
        <style>{`
          @keyframes float3D {
            0%, 100% {
              transform:
                perspective(1000px)
                rotateX(16deg)
                rotateY(-12deg)
                rotateZ(1deg)
                translateY(0px);

              box-shadow:
                -12px 16px 28px rgba(26,23,20,0.24),
                -2px 4px 6px rgba(26,23,20,0.12),
                inset 1px 2px 2px rgba(255,255,255,0.15);
            }

            50% {
              transform:
                perspective(1000px)
                rotateX(16deg)
                rotateY(-12deg)
                rotateZ(1deg)
                translateY(-10px);

              box-shadow:
                -20px 26px 36px rgba(26,23,20,0.16),
                -4px 8px 12px rgba(26,23,20,0.08),
                inset 1px 2px 2px rgba(255,255,255,0.25);
            }
          }

          @keyframes dashFlow {
            to {
              stroke-dashoffset: -32;
            }
          }

          .process-card-3d {
            transform-style: preserve-3d;
            animation: float3D 1.8s ease-in-out infinite;
            transition:
              transform 0.4s cubic-bezier(0.25, 1, 0.5, 1),
              box-shadow 0.4s cubic-bezier(0.25, 1, 0.5, 1);
          }

          .process-icon-container {
            transform: translateZ(25px);
          }

          @media (min-width: 1024px) {
            .process-card-3d:hover {
              transform:
                perspective(1000px)
                rotateX(4deg)
                rotateY(2deg)
                rotateZ(0deg)
                translateY(-20px)
                scale(1.08) !important;

              box-shadow:
                0 35px 60px rgba(26,23,20,0.3),
                0 10px 20px rgba(26,23,20,0.1) !important;

              animation-play-state: paused;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .process-card-3d {
              animation: none;
            }
          }
        `}</style>

        {/* Heading */}
        <div className="text-center">
          <p
            className="
              mb-3
              text-[10px]
              font-bold
              uppercase
              tracking-[0.25em]
              text-[#C8762A]

              sm:text-[11px]
              sm:tracking-[0.3em]
            "
          >
            How We Work
          </p>

          <h2
            className="
              mb-10
              text-[28px]
              font-bold
              text-[#1A1714]

              sm:mb-14
              sm:text-3xl

              md:text-4xl

              lg:mb-20
            "
          >
            Our Process
          </h2>
        </div>

        {/* Mobile Process Grid */}
        <div
          className="
            mx-auto
            grid
            max-w-lg
            grid-cols-2
            gap-x-4
            gap-y-10

            sm:grid-cols-3

            md:hidden
          "
        >
          {processSteps.map((step, index) => {
            const Icon = step.icon;

            return (
              <div
                key={step.num}
                className={`
                  group
                  flex
                  flex-col
                  items-center
                  text-center

                  ${
                    index === processSteps.length - 1
                      ? "col-span-2 sm:col-span-1"
                      : ""
                  }
                `}
              >
                <div
                  className="
                    process-card-3d
                    relative
                    flex
                    h-20
                    w-20
                    items-center
                    justify-center
                    rounded-2xl
                    text-white

                    sm:h-24
                    sm:w-24
                    sm:rounded-3xl
                  "
                  style={{
                    background:
                      "linear-gradient(135deg, #2E2822 0%, #161412 100%)",
                    border: "1px solid rgba(200, 118, 42, 0.15)",
                    animationDelay: `${index * 0.15}s`,
                  }}
                >
                  <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/0 via-white/[0.03] to-white/[0.08] sm:rounded-3xl" />

                  <div className="process-icon-container flex items-center justify-center">
                    <Icon className="h-7 w-7 text-[#C8762A] drop-shadow-[0_4px_6px_rgba(0,0,0,0.6)] sm:h-9 sm:w-9" />
                  </div>

                  <span
                    className="
                      absolute
                      -bottom-3
                      left-1/2
                      -translate-x-1/2
                      rounded-xl
                      border
                      border-white/10
                      bg-gradient-to-r
                      from-[#D68537]
                      to-[#B3631D]
                      px-2.5
                      py-1
                      text-[9px]
                      font-black
                      tracking-wider
                      text-white
                      shadow-[0_4px_10px_rgba(200,118,42,0.35)]

                      sm:px-3
                      sm:text-[10px]
                    "
                  >
                    {step.num}
                  </span>
                </div>

                <h3 className="mt-6 text-xs font-bold tracking-wide text-[#1A1714] sm:mt-8 sm:text-sm">
                  {step.title}
                </h3>
              </div>
            );
          })}
        </div>

        {/* Desktop Process Row */}
        <div className="relative mx-auto hidden max-w-7xl pb-10 pt-4 md:block">
          <div className="relative px-4">
            <svg
              className="pointer-events-none absolute left-0 top-[50px] h-[6px] w-full opacity-80"
              viewBox="0 0 1200 6"
              preserveAspectRatio="none"
            >
              <line
                x1="80"
                y1="3"
                x2="1120"
                y2="3"
                stroke="url(#gradient3d)"
                strokeWidth="4"
                strokeDasharray="12 10"
                strokeLinecap="round"
                style={{
                  animation: "dashFlow 1.6s linear infinite",
                }}
              />

              <defs>
                <linearGradient
                  id="gradient3d"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    stopColor="#C8762A"
                    stopOpacity="0.4"
                  />

                  <stop
                    offset="50%"
                    stopColor="#C8762A"
                    stopOpacity="1"
                  />

                  <stop
                    offset="100%"
                    stopColor="#C8762A"
                    stopOpacity="0.4"
                  />
                </linearGradient>
              </defs>
            </svg>

            <div className="relative flex items-start justify-between gap-4">
              {processSteps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <div
                    key={step.num}
                    className="group flex w-[110px] flex-col items-center text-center lg:w-[150px]"
                  >
                    <div
                      className="
                        process-card-3d
                        relative
                        flex
                        h-24
                        w-24
                        items-center
                        justify-center
                        rounded-3xl
                        text-white

                        lg:h-28
                        lg:w-28
                      "
                      style={{
                        background:
                          "linear-gradient(135deg, #2E2822 0%, #161412 100%)",
                        border: "1px solid rgba(200, 118, 42, 0.15)",
                        animationDelay: `${index * 0.25}s`,
                      }}
                    >
                      <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-tr from-white/0 via-white/[0.03] to-white/[0.08]" />

                      <div className="process-icon-container flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                        <Icon className="h-9 w-9 text-[#C8762A] drop-shadow-[0_4px_6px_rgba(0,0,0,0.6)] lg:h-10 lg:w-10" />
                      </div>

                      <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-xl border border-white/10 bg-gradient-to-r from-[#D68537] to-[#B3631D] px-3 py-1 text-[10px] font-black tracking-wider text-white shadow-[0_4px_10px_rgba(200,118,42,0.35)]">
                        {step.num}
                      </span>
                    </div>

                    <h3 className="mt-8 text-sm font-bold tracking-wide text-[#1A1714] transition-colors duration-300 group-hover:text-[#C8762A] lg:text-base">
                      {step.title}
                    </h3>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          WHY WORK WITH US
      ===================================================== */}

      <section
        className="
          bg-[#1A1714]
          px-5
          py-12
          text-center
          text-white

          sm:px-6
          sm:py-16

          lg:py-20
        "
      >
        <p
          className="
            mb-3
            text-[10px]
            font-bold
            uppercase
            tracking-[0.25em]
            text-[#C8762A]

            sm:text-[11px]
            sm:tracking-[0.3em]
          "
        >
          Why Now
        </p>

        <h2
          className="
            mx-auto
            mb-4
            max-w-3xl
            text-[28px]
            font-bold
            leading-tight

            sm:text-3xl

            md:text-4xl
          "
        >
          Why Work With a Studio Like Ours
        </h2>

        <p
          className="
            mx-auto
            mb-8
            max-w-2xl
            text-sm
            leading-6
            text-stone-400

            sm:mb-12
            sm:text-base
          "
        >
          We're not the biggest name yet — that's exactly why projects get more
          attention, faster decisions, and a team that's invested in getting it
          right.
        </p>

        <div
          className="
            mx-auto
            grid
            max-w-7xl
            grid-cols-1
            gap-4

            sm:grid-cols-2
            sm:gap-6

            md:grid-cols-3

            lg:gap-8
          "
        >
          {/* Engineering */}
          <div className="rounded-xl bg-white/10 p-5 shadow transition sm:p-6 lg:p-8 lg:hover:shadow-xl">
            <Code2 className="mx-auto mb-3 h-10 w-10 text-[#C8762A] sm:h-12 sm:w-12 lg:h-14 lg:w-14" />

            <h3 className="mb-2 text-lg font-semibold sm:text-xl">
              Hands-On Engineering
            </h3>

            <p className="text-sm leading-6 text-stone-400 sm:text-base">
              The people who scope your project are the ones who build it.
            </p>
          </div>

          {/* Fast Iteration */}
          <div className="rounded-xl bg-white/10 p-5 shadow transition sm:p-6 lg:p-8 lg:hover:shadow-xl">
            <Rocket className="mx-auto mb-3 h-10 w-10 text-[#C8762A] sm:h-12 sm:w-12 lg:h-14 lg:w-14" />

            <h3 className="mb-2 text-lg font-semibold sm:text-xl">
              Fast Iteration Cycles
            </h3>

            <p className="text-sm leading-6 text-stone-400 sm:text-base">
              No internal bureaucracy — changes ship in days, not sprints stuck
              in review.
            </p>
          </div>

          {/* Communication */}
          <div
            className="
              rounded-xl
              bg-white/10
              p-5
              shadow
              transition

              sm:col-span-2
              sm:p-6

              md:col-span-1

              lg:p-8
              lg:hover:shadow-xl
            "
          >
            <Headphones className="mx-auto mb-3 h-10 w-10 text-[#C8762A] sm:h-12 sm:w-12 lg:h-14 lg:w-14" />

            <h3 className="mb-2 text-lg font-semibold sm:text-xl">
              Direct Communication
            </h3>

            <p className="text-sm leading-6 text-stone-400 sm:text-base">
              Talk straight to your developer, not a support queue.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}

      <section
        className="
          bg-[#C8762A]
          px-5
          py-12
          text-center
          text-white

          sm:px-6
          sm:py-16

          lg:py-20
        "
      >
        <h2
          className="
            mx-auto
            mb-4
            max-w-3xl
            text-[28px]
            font-bold
            leading-tight

            sm:text-3xl

            md:text-4xl
          "
        >
          Let's Build Something Extraordinary
        </h2>

        <p
          className="
            mx-auto
            mb-7
            max-w-2xl
            text-sm
            leading-6
            text-white/90

            sm:mb-8
            sm:text-base

            md:text-lg
          "
        >
          Contact us today and get your project architected, estimated, and
          shipped with a team that treats it like their own.
        </p>

        <Link
          to="/contact"
          className="
            inline-flex
            items-center
            justify-center
            rounded-full
            bg-[#1A1714]
            px-7
            py-3.5
            text-sm
            font-semibold
            text-white
            shadow-md
            transition

            sm:px-10
            sm:py-4
            sm:text-base

            lg:hover:-translate-y-1
            lg:hover:bg-black
            lg:hover:shadow-2xl
          "
        >
          Get in Touch
        </Link>
      </section>

      <Footer />
    </div>
  );
}